/**
 * RoleAssignmentPermissionsSection Component
 *
 * Displays and manages permissions for a role assignment.
 * Shows current permissions with delete buttons and an add button.
 */

import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useRoleAssignmentPermissions } from '@/lib/hooks/usePermissions';
import { type PermissionsGrant } from '@anchorbase/pillar-sdk';
import { PermissionGrantSummary } from '@/components/permissions/PermissionGrantSummary';
import { Plus, Minus, Pencil } from 'lucide-react';

interface RoleAssignmentPermissionsSectionProps {
    roleAssignmentUuid: string;
    onAddPermission: () => void;
    onEditPermission: (permission: PermissionsGrant) => void;
    onRevokePermission: (permission: PermissionsGrant) => void;
}

export function RoleAssignmentPermissionsSection({
    roleAssignmentUuid,
    onAddPermission,
    onEditPermission,
    onRevokePermission,
}: RoleAssignmentPermissionsSectionProps) {
    const { data: roleAssignmentData, isLoading } =
        useRoleAssignmentPermissions(roleAssignmentUuid);
    const permissions: PermissionsGrant[] =
        roleAssignmentData?.permissions_bundle?.permissions || [];
    const organizationNameByUuid: Readonly<Record<string, string>> | undefined =
        roleAssignmentData?.organization_uuid !== null &&
        roleAssignmentData?.organization_uuid !== undefined &&
        roleAssignmentData.organization_name !== null
            ? {
                  [roleAssignmentData.organization_uuid]:
                      roleAssignmentData.organization_name,
              }
            : undefined;

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <Label>Permissions</Label>
                <div className="flex items-center gap-2">
                    <Button onClick={onAddPermission} size="sm" variant="outline">
                        <Plus className="h-4 w-4 mr-1" />
                        Add
                    </Button>
                </div>
            </div>
            {isLoading ? (
                <p className="text-sm text-muted-foreground">Loading permissions...</p>
            ) : permissions.length === 0 ? (
                <p className="text-sm text-muted-foreground">No permissions granted</p>
            ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto border rounded p-2">
                    {permissions.map((permission, index) => (
                        <div
                            key={index}
                            className="flex items-start justify-between gap-2 p-2 border-b last:border-b-0"
                        >
                            <div className="flex-1 min-w-0">
                                <PermissionGrantSummary
                                    permission={permission}
                                    compact
                                    organizationNameByUuid={organizationNameByUuid}
                                />
                            </div>
                            <div className="flex gap-1 flex-shrink-0">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => onEditPermission(permission)}
                                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                    title="Edit permission"
                                >
                                    <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => onRevokePermission(permission)}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    title="Remove permission"
                                >
                                    <Minus className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
